import type { ReactNode } from "react";

import clsx from "clsx";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useFocusTrap, type UseFocusTrapOptions } from "../hooks/useFocusTrap";
import { ArrowLeftIcon, CrossIcon } from "../Icons";
import { Typo } from "../Typo";

export type BottomSheetTitleType = "labelMD" | "headingMD";

export type BottomSheetDialogProps = {
  title?: string;
  ariaLabel?: string;
} & (
  | { trapFocus?: false }
  | ({ trapFocus?: boolean } & ({ title: string } | { ariaLabel: string }))
);

export type BottomSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Optional supporting text rendered under the title, inside the measured header */
  subtitle?: ReactNode;
  /** Title typography (default: "labelMD") */
  titleType?: BottomSheetTitleType;
  children: ReactNode;
  footer?: ReactNode;
  /** Optional back button handler — renders a back button in the header */
  onBack?: () => void;
  /** Label for the back button (defaults to "Back") */
  backLabel?: string;
  isDraggable?: boolean; // Optional prop to enable dragging the bottom sheet
  contentContainerClassName?: string; // Optional prop to customize content container styles
  isDismissable?: boolean; // Optional prop to enable dismissing the bottom sheet by clicking outside
  isFullScreen?: boolean;
  /**
   * Close (X) button visibility. Left undefined, a titled non-draggable sheet
   * shows it and an untitled sheet hides it. `true` shows it on an untitled
   * sheet; `false` hides it on a titled one.
   */
  showCloseButton?: boolean;
  closeButtonTestId?: string;
  initialFocus?: UseFocusTrapOptions["initialFocus"];
} & BottomSheetDialogProps;

type AnimationState = "closed" | "opening" | "open" | "closing";

const TOP_OFFSET = 104;
const SWIPE_THRESHOLD = 50;
const VIEWPORT_HEIGHT =
  typeof CSS !== "undefined" && CSS.supports?.("height", "100dvh")
    ? "100dvh"
    : "100vh";

const useMeasuredHeight = <T extends HTMLElement>() => {
  const [node, setNode] = useState<T | null>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (!node) return;

    // oxlint-disable-next-line react/set-state-in-effect
    setHeight(node.offsetHeight);

    const observer = new ResizeObserver(() => {
      setHeight(node.offsetHeight);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return [setNode, node ? height : 0] as const;
};

export const BottomSheet = ({
  isOpen,
  onClose,
  title,
  ariaLabel,
  subtitle,
  titleType = "labelMD",
  children,
  footer,
  onBack,
  backLabel = "Back",
  isDraggable = false,
  contentContainerClassName = "",
  isDismissable = true,
  isFullScreen = false,
  showCloseButton,
  closeButtonTestId,
  trapFocus = false,
  initialFocus = "none",
}: BottomSheetProps) => {
  const [animationState, setAnimationState] =
    useState<AnimationState>("closed");
  const pointerStartY = useRef<number | null>(null);
  const hasBeenOpen = useRef(false);
  const [handleRef, handleHeight] = useMeasuredHeight<HTMLDivElement>();
  const [titleRef, titleHeight] = useMeasuredHeight<HTMLDivElement>();
  const [footerRef, footerHeight] = useMeasuredHeight<HTMLDivElement>();
  const headerHeight = handleHeight + titleHeight;
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useBodyScrollLock(isOpen);
  useFocusTrap(panelRef, trapFocus && isOpen && animationState !== "closed", {
    initialFocus,
  });

  useEffect(() => {
    if (!isOpen || !isDismissable) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Capture phase + stopPropagation so this only closes the topmost
      // layer: it runs before any bubble-phase listener beneath it (e.g. a
      // Drawer's own Escape handler) gets a chance to also fire.
      e.stopPropagation();
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, isDismissable, onClose]);

  // Reset pointer state when component closes or unmounts
  useEffect(() => {
    if (!isOpen) {
      pointerStartY.current = null;
    }
    return () => {
      pointerStartY.current = null;
    };
  }, [isOpen]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    pointerStartY.current = e.clientY;

    // Capture pointer to receive all events even if pointer leaves element
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartY.current === null) return;

    const deltaY = e.clientY - pointerStartY.current;

    if (deltaY > SWIPE_THRESHOLD) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      pointerStartY.current = null;
      onClose();
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartY.current !== null) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      pointerStartY.current = null;
    }
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    pointerStartY.current = null;
  };

  useEffect(() => {
    if (isOpen) {
      hasBeenOpen.current = true;
      // oxlint-disable-next-line react/set-state-in-effect
      setAnimationState("opening");
      const timer = setTimeout(() => setAnimationState("open"), 100);
      return () => clearTimeout(timer);
    }

    if (!hasBeenOpen.current) return;

    setAnimationState("closing");
    const timer = setTimeout(() => setAnimationState("closed"), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  if (animationState === "closed") return null;

  const maxHeight = isFullScreen
    ? VIEWPORT_HEIGHT
    : `calc(${VIEWPORT_HEIGHT} - ${TOP_OFFSET}px)`;
  const contentMaxHeight = `calc(${maxHeight} - ${headerHeight}px - ${footerHeight}px)`;
  const isTitleRendered = Boolean(title) && !onBack;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        data-testid="modal_backdrop"
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
          animationState === "open" ? "opacity-100" : "opacity-0"
        }`}
        // This sheet renders via a portal, so its DOM sits outside whatever
        // opened it — but React bubbles synthetic events through the
        // *component* tree, not the DOM tree. A caller that opens this sheet
        // from inside a clickable row/card (e.g. an info trigger nested in a
        // table row) would otherwise see a backdrop dismiss click also reach
        // that row's own onClick.
        onClick={
          isDismissable
            ? (e) => {
                e.stopPropagation();
                onClose();
              }
            : undefined
        }
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        data-testid="bottom_sheet_panel"
        role={trapFocus ? "dialog" : undefined}
        aria-modal={trapFocus ? true : undefined}
        aria-labelledby={trapFocus && isTitleRendered ? titleId : undefined}
        aria-label={
          trapFocus && !isTitleRendered ? ariaLabel || title : undefined
        }
        tabIndex={trapFocus ? -1 : undefined}
        className={clsx(
          "relative w-full transform rounded-t-3xl bg-surface-primary outline-none transition-transform duration-300 ease-out",
          animationState === "open" ? "translate-y-0" : "translate-y-full",
          isFullScreen && "flex flex-col",
        )}
        style={isFullScreen ? { height: maxHeight, maxHeight } : { maxHeight }}
        // Catches bubbling from every descendant in one place — header, body
        // (`children`), and `footer` content all live under this panel, and
        // none of them can rely on the DOM tree to isolate them from the
        // opener (the backdrop above is a sibling, not a descendant, so it
        // still needs its own stop).
        onClick={(e) => e.stopPropagation()}
      >
        {isDraggable && (
          <div
            ref={handleRef}
            data-testid="draggable-handle"
            className="flex cursor-grab touch-none flex-col items-center p-2"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
          >
            <div className="h-1 w-12 rounded-full bg-surface-tonal-bold" />
          </div>
        )}
        {(title || onBack) && (
          <div
            ref={titleRef}
            className={clsx(
              "flex flex-col gap-2 px-4 pb-2",
              // The drag handle already contributes 8px below its bar (its
              // `p-2`), which is the full gap the design calls for — so the
              // title adds no top padding of its own when draggable. Without
              // a handle the title still needs its own breathing room from
              // the sheet's rounded top edge.
              isDraggable ? "pt-0" : "pt-4",
            )}
          >
            <div className="flex items-center justify-between">
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="flex items-center gap-1 text-text-primary"
                  aria-label={backLabel}
                >
                  <ArrowLeftIcon className="text-text-primary" />
                  <Typo type="labelMD" textColor="text-text-primary">
                    {backLabel}
                  </Typo>
                </button>
              ) : (
                <div id={titleId}>
                  <Typo type={titleType} textColor="text-text-primary">
                    {title}
                  </Typo>
                </div>
              )}
              {!isDraggable && showCloseButton !== false && (
                <button
                  type="button"
                  aria-label="Close"
                  onClick={onClose}
                  data-testid={closeButtonTestId}
                  className="flex h-6 w-6 shrink-0 items-center justify-center text-icon-primary"
                >
                  <CrossIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              )}
            </div>
            {subtitle && (
              <Typo type="bodySM" textColor="text-text-secondary">
                {subtitle}
              </Typo>
            )}
          </div>
        )}
        {!title && !onBack && showCloseButton === true && (
          <div ref={titleRef} className="flex justify-end px-4 pb-2 pt-4">
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              data-testid={closeButtonTestId}
              className="flex h-6 w-6 shrink-0 items-center justify-center text-icon-secondary"
            >
              <CrossIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        )}

        <div
          className={clsx(
            "overflow-y-auto p-4",
            isFullScreen && "min-h-0 flex-1",
            contentContainerClassName,
          )}
          style={isFullScreen ? undefined : { maxHeight: contentMaxHeight }}
        >
          {children}
        </div>

        {footer && (
          <div
            ref={footerRef}
            className={clsx(
              "sticky bottom-0 w-full bg-surface-primary px-4 pb-4 pt-3",
              isFullScreen && "shrink-0",
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
